import type { ReactNode } from "react"
import { Logo } from "@/components/Logo"

// <CheckoutLayout>
//
// Clean, distraction-free two-column layout for the checkout flow. Left-
// aligned Flywrite wordmark on bg-surface, then a grid where each column is
// a framed page-colored panel (rounded, hairline border, bg-page) with an
// optional slightly-darker header band on top. Colors are inverted from the
// rest of the app — page bg is surface, panel bg is page — so the framed
// cards pop forward against the softer outer field.
//
// Props:
//   mainHeader?    — the top band of the main column (e.g. <CheckoutProgress>)
//   main           — the main body (account or payment form)
//   sidebarHeader? — the top band of the sidebar column (e.g. "Purchase Details")
//   sidebar?       — the sidebar body (e.g. <PurchaseDetailsCard>); when
//                    omitted the main panel spans a centered single column.
//
// The sidebar stacks below the main panel on mobile and sticks to the top
// on desktop.
export function CheckoutLayout({
  mainHeader,
  main,
  sidebarHeader,
  sidebar,
}: {
  mainHeader?: ReactNode
  main: ReactNode
  sidebarHeader?: ReactNode
  sidebar?: ReactNode
}) {
  return (
    <main className="min-h-svh bg-surface px-4 py-10 sm:px-6">
      <div className={`mx-auto w-full ${sidebar ? "max-w-5xl" : "max-w-2xl"} space-y-8`}>
        <a
          href="https://flywrite.app"
          aria-label="Flywrite"
          className="flex w-fit items-center no-underline"
        >
          <Logo className="h-9" />
        </a>
        {sidebar ? (
          <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
            <Panel header={mainHeader}>{main}</Panel>
            <aside className="h-fit lg:sticky lg:top-6">
              <Panel header={sidebarHeader}>{sidebar}</Panel>
            </aside>
          </div>
        ) : (
          <Panel header={mainHeader}>{main}</Panel>
        )}
      </div>
    </main>
  )
}

function Panel({ header, children }: { header?: ReactNode; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-lg border border-hairline bg-page">
      {header && <div className="border-b border-hairline bg-hairline/60 px-6 py-4">{header}</div>}
      <div className="p-6">{children}</div>
    </div>
  )
}
